import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import { OAuthConsentFeatureGate } from '../../../FeatureGates/FeatureGates';
import { MCP_INTENT } from './intents';
import { getVisibleToolsetNames, mergeMcpPolicyIntents } from './policyParse';
import type { McpPolicySource, PolicyState } from './policyState';
import type { McpPolicyIntentGroup } from './policyTypes';

/**
 * The toolsets the user has selected: every check's toolsets are added as it
 * loads, and the permissions list toggles them one by one.
 */
export function useToolsetSelection() {
  const [selectedNames, setSelectedNames] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const selectFetchedToolsets = useCallback((toolsetNames: Set<string>) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      for (const name of toolsetNames) next.add(name);
      return next;
    });
  }, []);

  const toggleMcpToolset = useCallback(
    (toolsetName: string, selected: boolean) => {
      setSelectedNames((prev) => {
        const next = new Set(prev);
        if (selected) next.add(toolsetName);
        else next.delete(toolsetName);
        return next;
      });
    },
    [],
  );

  const keepVisibleToolsets = useCallback(
    (visibleNames: ReadonlySet<string>) => {
      setSelectedNames((prev) => {
        const next = new Set(
          [...prev].filter((name) => visibleNames.has(name)),
        );
        return next.size === prev.size ? prev : next;
      });
    },
    [],
  );

  return {
    selectedNames,
    selectFetchedToolsets,
    toggleMcpToolset,
    keepVisibleToolsets,
  };
}

/**
 * The permissions list: the site, Trello and Bitbucket results merged, limited
 * to the sources that speak for this flow and to the intent groups that are
 * rolled out, plus the toolsets selected within it.
 */
export function useMergedToolsets({
  site,
  trello,
  bitbucket,
  orderedPolicySources,
  isForgeMcp,
  selection: { selectedNames, keepVisibleToolsets },
}: {
  site: PolicyState;
  trello: PolicyState;
  bitbucket: PolicyState;
  orderedPolicySources: readonly McpPolicySource[];
  isForgeMcp: boolean;
  selection: Pick<
    ReturnType<typeof useToolsetSelection>,
    'selectedNames' | 'keepVisibleToolsets'
  >;
}) {
  const jiraDeleteToolsetsEnabled = useFeatureGate(
    OAuthConsentFeatureGate.MCP_JIRA_DELETE_TOOLSETS,
  );

  // Merge the site, Trello and Bitbucket policy byIntent arrays. All are
  // ordered read → write → search; merge toolsets within matching intents.
  // Delete is still rolling out, so it is hidden until its own gate is on.
  const byIntent = useMemo<McpPolicyIntentGroup[]>(() => {
    const intentsOf = (source: McpPolicySource, policy: PolicyState) =>
      orderedPolicySources.includes(source) && policy.status === 'success'
        ? policy.byIntent
        : [];
    const isIntentGroupEnabled = (intentId: string) => {
      if (intentId === MCP_INTENT.delete) return jiraDeleteToolsetsEnabled;
      return true;
    };
    return mergeMcpPolicyIntents([
      ...intentsOf('site', site),
      ...intentsOf('trello', trello),
      ...intentsOf('bitbucket', bitbucket),
    ]).filter((group) => isIntentGroupEnabled(group.id));
  }, [
    site,
    trello,
    bitbucket,
    orderedPolicySources,
    jiraDeleteToolsetsEnabled,
  ]);

  // Drop selected toolsets only when they are no longer visible in the merged
  // policy, e.g. after a workspace is unselected or a gate hides an intent.
  useEffect(() => {
    keepVisibleToolsets(getVisibleToolsetNames(byIntent));
  }, [byIntent, keepVisibleToolsets]);

  // Forge MCP permissions are intentionally read-only in the UI. Publish every
  // visible toolset as selected so the hidden resume fields submit the complete
  // grant, including toolsets that standard MCP asks users to opt into.
  const selectedToolsetNames = useMemo(
    () => (isForgeMcp ? getVisibleToolsetNames(byIntent) : selectedNames),
    [byIntent, isForgeMcp, selectedNames],
  );

  return { byIntent, selectedToolsetNames };
}
